import {
	ClassNameProps,
	Conditional,
	Else,
	Grid,
	If,
	makeClassName,
	Row,
	StyleProps,
	Tooltip,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Relationship } from "../../../../Utilities";
import { RelationshipFormat } from "../../../Format";
import styles from "./styles.module.css";

export type CardRelationship = Pick<
	Relationship,
	| "id"
	| "relationshipType"
	| "isPrimary"
	| "isAuthorizedForPickup"
	| "livesWithStudent"
	| "isEmergencyContact"
	| "isFinancialContact"
	| "hasCustody"
>;

export type RelationshipCardProps = {
	relationship: CardRelationship;
} & Partial<ClassNameProps & StyleProps>;

export function RelationshipCard({ relationship, className, style }: RelationshipCardProps) {
	return (
		<Grid
			tracks={2}
			gap="var(--general--spaced---vertical-spacing)"
			className={makeClassName(styles["relationship-card"], className)}
			style={style}
		>
			<TruncatedText className={styles["relationship-card__relationship-type"]}>
				{RelationshipFormat.Fields.relationshipType(relationship.relationshipType)}
			</TruncatedText>

			<Conditional>
				<If condition={relationship.isPrimary}>
					<Row justify="spaced-start" align="center" className={styles["relationship-card__property"]}>
						<Icon.Star size={18} weight="fill" color="var(--theme-colors__yellow)" />
						<TruncatedText>Primary Contact</TruncatedText>
					</Row>
				</If>
				<Else>
					<span>{/* flex placeholder */}</span>
				</Else>
			</Conditional>

			<If condition={relationship.isAuthorizedForPickup}>
				<Row justify="spaced-start" align="center" className={styles["relationship-card__property"]}>
					<Icon.CarProfile size={16} color="var(--theme-colors__blue)" />
					<TruncatedText>Authorized for Pickup</TruncatedText>
				</Row>
			</If>
			<If condition={relationship.livesWithStudent}>
				<Row justify="spaced-start" align="center" className={styles["relationship-card__property"]}>
					<Icon.House size={16} color="var(--theme-colors__blue)" />
					<TruncatedText>Lives with Student</TruncatedText>
				</Row>
			</If>
			<If condition={relationship.isEmergencyContact}>
				<Row justify="spaced-start" align="center" className={styles["relationship-card__property"]}>
					<Icon.Asterisk size={16} color="var(--theme-colors__pink)" />
					<TruncatedText>Emergency Contact</TruncatedText>
				</Row>
			</If>
			<If condition={relationship.isFinancialContact}>
				<Row justify="spaced-start" align="center" className={styles["relationship-card__property"]}>
					<Icon.Money size={16} color="var(--theme-colors__green)" />
					<TruncatedText>Financial Contact</TruncatedText>
				</Row>
			</If>
			<If condition={relationship.hasCustody}>
				<Row justify="spaced-start" align="center" className={styles["relationship-card__property"]}>
					<Icon.PersonArmsSpread size={16} color="var(--theme-colors__blue)" />
					<TruncatedText>Has Custody</TruncatedText>
				</Row>
			</If>
		</Grid>
	);
}

export function RelationshipCardSlim({ relationship, className, style }: RelationshipCardProps) {
	return (
		<Row justify="space-between" align="center" className={className} style={style}>
			<TruncatedText className={styles["relationship-card__relationship-type"]}>
				{RelationshipFormat.Fields.relationshipType(relationship.relationshipType)}
			</TruncatedText>

			<RelationshipPropertyIcons relationship={relationship} style={{ marginLeft: "0.75rem" }} />
		</Row>
	);
}

export function RelationshipPropertyIcons({ relationship, className, style }: RelationshipCardProps) {
	return (
		<Row justify="spaced-start" align="center" className={className} style={style}>
			<If condition={relationship.isPrimary}>
				<Tooltip.Container>
					<Tooltip className="tooltip--inverted">
						<Tooltip.Body>
							<span className="nowrap">Primary Contact</span>
						</Tooltip.Body>
					</Tooltip>
					<Icon.Star size={18} weight="fill" color="var(--theme-colors__yellow)" className="block" />
				</Tooltip.Container>
			</If>
			<If condition={relationship.isAuthorizedForPickup}>
				<Tooltip.Container>
					<Tooltip className="tooltip--inverted">
						<Tooltip.Body>
							<span className="nowrap">Authorized for Pickup</span>
						</Tooltip.Body>
					</Tooltip>
					<Icon.CarProfile size={16} color="var(--theme-colors__blue)" className="block" />
				</Tooltip.Container>
			</If>
			<If condition={relationship.livesWithStudent}>
				<Tooltip.Container>
					<Tooltip className="tooltip--inverted">
						<Tooltip.Body>
							<span className="nowrap">Lives with Student</span>
						</Tooltip.Body>
					</Tooltip>
					<Icon.House size={16} color="var(--theme-colors__blue)" className="block" />
				</Tooltip.Container>
			</If>
			<If condition={relationship.isEmergencyContact}>
				<Tooltip.Container>
					<Tooltip className="tooltip--inverted">
						<Tooltip.Body>
							<span className="nowrap">Emergency Contact</span>
						</Tooltip.Body>
					</Tooltip>
					<Icon.Asterisk size={16} color="var(--theme-colors__pink)" className="block" />
				</Tooltip.Container>
			</If>
			<If condition={relationship.isFinancialContact}>
				<Tooltip.Container>
					<Tooltip className="tooltip--inverted">
						<Tooltip.Body>
							<span className="nowrap">Financial Contact</span>
						</Tooltip.Body>
					</Tooltip>
					<Icon.Money size={16} color="var(--theme-colors__green)" className="block" />
				</Tooltip.Container>
			</If>
			<If condition={relationship.hasCustody}>
				<Tooltip.Container>
					<Tooltip className="tooltip--inverted">
						<Tooltip.Body>
							<span className="nowrap">Has Custody</span>
						</Tooltip.Body>
					</Tooltip>
					<Icon.PersonArmsSpread size={16} color="var(--theme-colors__blue)" className="block" />
				</Tooltip.Container>
			</If>
		</Row>
	);
}
